import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card } from '@components/ui';
import { useBurndownData } from '@hooks';
import { formatDay } from '@utils/calendarDate';

export default function BurndownChart({ sprintId, height = 300 }) {
  const { data: burndownData, isLoading, error } = useBurndownData(sprintId);

  const chartData = useMemo(() => {
    if (!burndownData?.data?.burndown) return null;
    return burndownData.data;
  }, [burndownData]);

  if (!sprintId) {
    return (
      <Card padding="lg">
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
          📉 Burndown Chart
        </h3>
        <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>Selecciona un sprint activo</p>
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card padding="lg">
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
          📉 Burndown Chart
        </h3>
        <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>Cargando...</p>
        </div>
      </Card>
    );
  }

  if (error || !chartData) {
    return (
      <Card padding="lg">
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
          📉 Burndown Chart
        </h3>
        <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: '#ef4444', fontSize: '14px' }}>Error al cargar datos</p>
        </div>
      </Card>
    );
  }

  const { burndown, totalPoints, sprint } = chartData;
  const maxPoints = totalPoints || Math.max(...burndown.map((d) => d.ideal));
  const chartWidth = 100;
  const chartHeight = height - 80;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Generate SVG path for ideal line
  const idealPath = burndown
    .map((d, i) => {
      const x = padding.left + (i / (burndown.length - 1)) * innerWidth;
      const y = padding.top + (1 - d.ideal / maxPoints) * innerHeight;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // Generate SVG path for actual line (only points with data)
  const actualPoints = burndown.filter((d) => d.actual !== null);
  const actualPath = actualPoints
    .map((d, i) => {
      const originalIndex = burndown.indexOf(d);
      const x = padding.left + (originalIndex / (burndown.length - 1)) * innerWidth;
      const y = padding.top + (1 - d.actual / maxPoints) * innerHeight;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // X-axis labels (show first, middle, last dates)
  const xLabels = [0, Math.floor(burndown.length / 2), burndown.length - 1]
    .filter((i) => burndown[i])
    .map((i) => ({
      x: padding.left + (i / (burndown.length - 1)) * innerWidth,
      label: formatDay(burndown[i].date, { day: 'numeric', month: 'short' }),
    }));

  // Y-axis labels
  const yLabels = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
    y: padding.top + (1 - ratio) * innerHeight,
    label: Math.round(maxPoints * ratio),
  }));

  return (
    <Card padding="lg">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
          📉 Burndown Chart
        </h3>
        <span style={{ fontSize: '13px', color: '#64748b' }}>
          {sprint.name}
        </span>
      </div>

      <div style={{ position: 'relative', width: '100%', height }}>
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: '100%' }}
        >
          {/* Grid lines */}
          {yLabels.map((label, i) => (
            <line
              key={i}
              x1={padding.left}
              y1={label.y}
              x2={chartWidth - padding.right}
              y2={label.y}
              stroke="#e2e8f0"
              strokeWidth="0.3"
            />
          ))}

          {/* Y-axis labels */}
          {yLabels.map((label, i) => (
            <text
              key={i}
              x={padding.left - 5}
              y={label.y + 1}
              textAnchor="end"
              style={{ fontSize: '3px', fill: '#64748b' }}
            >
              {label.label}
            </text>
          ))}

          {/* X-axis labels */}
          {xLabels.map((label, i) => (
            <text
              key={i}
              x={label.x}
              y={chartHeight - padding.bottom + 10}
              textAnchor="middle"
              style={{ fontSize: '3px', fill: '#64748b' }}
            >
              {label.label}
            </text>
          ))}

          {/* Ideal line */}
          <path d={idealPath} fill="none" stroke="#94a3b8" strokeWidth="0.5" strokeDasharray="2,1" />

          {/* Actual line */}
          {actualPath && (
            <path d={actualPath} fill="none" stroke="#667eea" strokeWidth="0.8" strokeLinecap="round" />
          )}

          {/* Actual points */}
          {actualPoints.map((d, i) => {
            const originalIndex = burndown.indexOf(d);
            const x = padding.left + (originalIndex / (burndown.length - 1)) * innerWidth;
            const y = padding.top + (1 - d.actual / maxPoints) * innerHeight;
            return <circle key={i} cx={x} cy={y} r="1" fill="#667eea" />;
          })}
        </svg>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', marginTop: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '24px', height: '2px', backgroundColor: '#94a3b8' }} />
          <span style={{ fontSize: '12px', color: '#64748b' }}>Ideal</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '24px', height: '2px', backgroundColor: '#667eea' }} />
          <span style={{ fontSize: '12px', color: '#64748b' }}>Real</span>
        </div>
      </div>
    </Card>
  );
}

BurndownChart.propTypes = {
  sprintId: PropTypes.number,
  height: PropTypes.number,
};
